/**
 * Analytics collaboration projects — CRUD against the playground API.
 *
 * Every call goes through the shared `api()` helper, which handles
 * credentials, CSRF, error unwrapping and the {success, data} envelope.
 */
import { api } from './api'

export interface AnalyticsProject {
  id: number
  owner_id: number
  title: string
  org_id: number | null
  game_code: string | null
  tab: string | null
  draft: Record<string, unknown> | null
  is_default: boolean
  /** Visibility: false = owner-only, true = every Lab user can OPEN it
   *  read-only. Editing stays owner-only either way. */
  shared: boolean
  /** Whether THIS caller may edit — false on someone else's shared
   *  project, and the client must not autosave drafts into it. */
  can_edit?: boolean
  owner_name?: string | null
  created_at: string | null
  updated_at: string | null
}

interface ProjectList {
  projects: AnalyticsProject[]
  /** Other people's shared projects — a separate list so own and borrowed
   *  can never be confused. */
  shared_projects?: AnalyticsProject[]
}
interface ProjectSingle { project: AnalyticsProject }

export function listProjects(): Promise<ProjectList> {
  return api<ProjectList>('/api/v1/playground/projects')
}

export function getProject(id: number): Promise<ProjectSingle> {
  return api<ProjectSingle>(`/api/v1/playground/projects/${id}`)
}

export function createProject(data: {
  title: string
  org_id?: number | null
  game_code?: string | null
  tab?: string | null
  draft?: Record<string, unknown> | null
  is_default?: boolean
}): Promise<ProjectSingle> {
  return api<ProjectSingle>('/api/v1/playground/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function updateProject(id: number, data: {
  title?: string
  org_id?: number | null
  game_code?: string | null
  tab?: string | null
  draft?: Record<string, unknown> | null
  is_default?: boolean
  shared?: boolean
}): Promise<ProjectSingle> {
  return api<ProjectSingle>(`/api/v1/playground/projects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

/** Copy a project (yours, or a shared one) into a new project you own —
 *  the copy starts private and non-default. Omit title for "{title} (copy)". */
export function duplicateProject(id: number, title?: string): Promise<ProjectSingle> {
  return api<ProjectSingle>(`/api/v1/playground/projects/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify(title ? { title } : {}),
  })
}

export function deleteProject(id: number): Promise<unknown> {
  return api(`/api/v1/playground/projects/${id}`, { method: 'DELETE' })
}

// ── Canvas types ────────────────────────────────────────────────────────────

/** A companion visualization worth adding as its own card. */
export interface VisualSuggestion {
  label: string
  question: string
}

export interface CanvasCard {
  id: string
  /** 'query' = NL/SQL result card, 'note' = free-text note. */
  kind: 'query' | 'note'
  question: string
  sql: string
  explanation: string
  /** 'local' = pattern engine, 'llm' = Claude, '' = manual/note. */
  source: string
  result: QueryResult | null
  error: string | null
  visual: VisualKind
  mapping: Record<string, string | undefined>
  /** Free-text note content (for note cards). */
  note: string
  /** Performance diagnostics from the NL query pipeline. */
  perf: QueryPerf | null
  /** The pitch region the data was filtered to (e.g. 'attacking third'),
   *  so the chart can zoom to that slice. */
  region: string
  /** Companion visualizations offered as one-click new cards. */
  suggestions: VisualSuggestion[]
  position: { x: number; y: number }
  size: { w: number; h: number }
}

export type VisualKind =
  | 'table' | 'bar' | 'line' | 'scatter' | 'pitch'
  | 'heatmap' | 'flow' | 'histogram' | 'pitchzones'
  | 'shape' | 'spacemap' | 'timeline' | 'passnet' | 'rose'

/** The server's statement of how a result maps onto the pitch — column
 *  roles plus the coordinate frame (the parquets mix meters and
 *  centimeters, so the client must not assume). Absent/null means the
 *  result carries no pitch coordinates at all. */
export type SpatialRole = 'x' | 'y' | 'x2' | 'y2' | 'team' | 'player' | 'frame' | 'size'
export interface SpatialContract {
  roles: Partial<Record<SpatialRole, string>>
  frame: {
    origin: 'center' | 'corner'; units: 'm' | 'cm'; units_divisor: number
    view?: 'canonical' | 'attacking' | 'camera'
  }
  plottable: VisualKind[]
  source: 'declared' | 'inferred'
  detail: string
}

export interface QueryResult {
  columns: { name: string; type: string }[]
  rows: unknown[][]
  row_count: number
  truncated: boolean
  elapsed_ms: number
  tables: string[]
  spatial?: SpatialContract | null
}

/** One analytics result pinned onto the shared Pitch Board. The data is a
 *  snapshot (columns + rows + the mapping it was drawn with), so the layer
 *  survives the card it came from being edited or removed. */
export interface PitchLayer {
  id: string
  /** The question that produced it — the legend label. */
  label: string
  /** How to draw it: the visual kind of the card it came from. */
  kind: VisualKind
  color: string
  visible: boolean
  columns: { name: string; type: string }[]
  rows: unknown[][]
  mapping: Record<string, string | undefined>
  /** The pitch region this data was filtered to (e.g. 'attacking third'),
   *  so the board zooms to that slice instead of a full pitch. */
  region?: string
}

export interface CanvasDraft {
  cards: CanvasCard[]
  /** Layers on the always-visible pitch column. Absent in older drafts. */
  pitchLayers?: PitchLayer[]
  [key: string]: unknown
}

// ── Checklist ────────────────────────────────────────────────────────────────

export interface ChecklistItem {
  id: string
  text: string
  done: boolean
  /** Nesting depth: 0 = top-level, 1 = child, 2 = grandchild. */
  depth: number
}

// ── NL query ────────────────────────────────────────────────────────────────

export interface QueryPerf {
  total_ms: number
  meta_ms: number
  translate_ms: number
  query_ms: number
  row_count?: number
  tables_scanned?: string[]
  warnings: string[]
  plan: string
}

export interface QueryPerspective {
  viewing: 'ours' | 'theirs'
  resolved: boolean
  reason?: string
  us_team_id?: number
  opp_team_id?: number
  home_team_id?: number
  us_name?: string
  opp_name?: string
}

export interface NLQueryResponse {
  question: string
  sql: string
  explanation: string
  source: string
  /** The engine's suggested visualization for this result ('' = table). */
  visual?: string
  /** The pitch region the data was filtered to (e.g. 'attacking third'),
   *  so the chart can clip/zoom to that slice instead of drawing the full
   *  pitch with a few scattered dots. */
  region?: string
  /** Companion visualizations worth adding as their own cards. */
  suggestions?: VisualSuggestion[]
  perspective?: QueryPerspective
  result: QueryResult | null
  error?: string
  perf?: QueryPerf
}

export function nlQuery(data: {
  question: string
  game_code: string
  org_id: number
  tables?: unknown[]
  limit?: number
  perspective?: 'ours' | 'theirs'
}): Promise<NLQueryResponse> {
  return api<NLQueryResponse>('/api/v1/playground/nl-query', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ── Direct SQL query (for editing generated SQL) ────────────────────────────

export function runQuery(data: {
  game_code: string
  org_id: number
  sql: string
  limit?: number
}): Promise<QueryResult> {
  return api<QueryResult>('/api/v1/playground/query', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ── Temp tables ─────────────────────────────────────────────────────────────

export interface TempTableResult {
  name: string
  row_count: number
  truncated: boolean
  columns: { name: string; type: string }[]
  expires_in_s: number
}

export function createTempTable(data: {
  game_code: string
  org_id: number
  sql: string
  project_id: number
  label: string
}): Promise<TempTableResult> {
  return api<TempTableResult>('/api/v1/playground/temp-table', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export interface TempTableInfo {
  name: string
  age_s: number
  expires_in_s: number
}

export function listTempTables(projectId: number): Promise<{ tables: TempTableInfo[] }> {
  return api<{ tables: TempTableInfo[] }>(`/api/v1/playground/temp-tables?project_id=${projectId}`)
}
